import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useParams, Link } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const SubBrandGallery = () => {
  const { brandName, subBrandSlug } = useParams();

  const [images, setImages] = useState([]);
  const [subBrand, setSubBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = `${process.env.REACT_APP_API_URL}/uploads/${brandName}/${subBrandSlug}`;
        const res = await fetch(url);

        if (!res.ok) {
          const errorText = await res.text();
          console.error(errorText);
          setError(`Failed to fetch: ${res.status} ${res.statusText}`);
          return;
        }

        const data = await res.json();
        const { subbrand, uploads } = data;

        setSubBrand(subbrand);
        setImages(uploads || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [brandName, subBrandSlug]);

  // ✅ open lightbox
  const openLightbox = (index) => {
    setSelectedIndex(index);
    setLightboxOpen(true);
  };

  // ✅ close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  // ✅ next image
  const nextImage = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  // ✅ prev image
  const prevImage = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  }, [images.length]);

  // ✅ keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;

      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") closeLightbox();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage]);

  return (
    <>
      <Helmet>
        <title>{`${subBrand?.name || subBrandSlug} | ${brandName} | Glintz Photography`}</title>
      </Helmet>

      <Navbar />

      {/* Header */}
      <div className="xl:px-44 px-6 pt-[100px]">
        <h1 className="uppercase tracking-[.25em] text-2xl">
          {subBrand?.name || subBrandSlug}
        </h1>
        <p className="text-gray-600 mt-2 text-sm">
          <Link to="/">Home</Link> /{" "}
          <Link to={`/portfolio/${brandName}`}>{brandName}</Link> /{" "}
          <span className="capitalize">
            {subBrand?.name || subBrandSlug}
          </span>
        </p>
      </div>

      {/* Images Section */}
      <div className="xl:px-20 px-6 mt-16 py-20">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-[300px]">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-black border-t-transparent"></div>
          </div>
        ) : images.length === 0 ? (
          <p className="text-center text-gray-500">
            No images uploaded yet.
          </p>
        ) : (
          <div className="grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
            {images.map((img, index) => (
              <div
                key={img._id}
                className="overflow-hidden group cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={img.image}
                  alt={img.subbrand?.name || "Gallery image"}
                  className="w-full h-[300px] object-cover group-hover:scale-105 transition duration-500"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Lightbox */}
      {lightboxOpen && images[selectedIndex] && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white text-4xl"
          >
            ×
          </button>

          {/* Prev */}
          <button
            onClick={prevImage}
            className="absolute left-6 text-white text-5xl"
          >
            ‹
          </button>

          {/* Image */}
          <img
            src={images[selectedIndex].image}
            alt="Preview"
            className="max-w-[90%] max-h-[85vh] object-contain"
          />

          {/* Next */}
          <button
            onClick={nextImage}
            className="absolute right-6 text-white text-5xl"
          >
            ›
          </button>

          {/* Counter */}
          <div className="absolute bottom-6 text-white text-sm tracking-widest">
            {selectedIndex + 1} / {images.length}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default SubBrandGallery;